"use client";

import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import {
  useInsuranceConnectorContext,
  useInsuranceConnectorSend,
  useNavButtonRef,
} from "../insurance-connector-provider";
import { InsuranceLogo } from "@frontend/components/InsuranceStepper/InsuranceLogo";
import { useBoundStore } from "@frontend/lib/stores/globalStore";
import { ArrowRightIcon } from "lucide-react";
import Image from "next/image";
import type { InsuranceStatus } from "@frontend/lib/schemas/clientSchema";
import type {
  InsuranceConnectorContext,
  InsuranceConnectorEvent,
} from "../machine/types";

interface InsuranceMenuProps {
  isMobile?: boolean;
}

function StatusBadge({
  verificationResult,
}: { verificationResult?: InsuranceStatus }) {
  if (!verificationResult) return null;

  const { status, network } = verificationResult;

  if (status === "active" && network === "IN") {
    return (
      <div className="flex flex-row items-center gap-2">
        <p className="text-xs text-black">Active, In-Network</p>
        <Image
          src="/images/activeOON.svg"
          alt="Active In-Network"
          width={42}
          height={20}
        />
      </div>
    );
  }

  if (status === "unverified") {
    return (
      <div className="flex flex-row items-center gap-2">
        <p className="text-xs text-black">Unverified</p>
        <Image
          src="/images/unverified.svg"
          alt="Unverified"
          width={42}
          height={20}
        />
      </div>
    );
  }

  return null;
}

function InsuranceCard({
  label,
  payer,
  memberId,
  planName,
  state,
  onDisconnect,
  verificationResult,
}: {
  label: string;
  payer: string;
  memberId: string;
  planName: string;
  state: string | null | undefined;
  onDisconnect: () => void;
  verificationResult?: InsuranceStatus;
}) {
  const t = useTranslations("InsuranceConnector");

  return (
    <div className="flex flex-col gap-2 rounded-2xl border bg-white p-4 h-[212px]">
      <div className="flex flex-row justify-between items-center">
        <p className="text-sm font-semibold text-black">{label}</p>
        <StatusBadge verificationResult={verificationResult} />
      </div>
      <div className="min-w-0 flex-1">
        <InsuranceLogo insuranceName={payer} icon={false} />
        <p className="truncate text-xs text-gray-500">
          {payer} {planName && "•"} {planName}
        </p>
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-gray-900">
          {t("StateMenuLabel")}
        </p>
        <p className="truncate text-xs text-gray-500">{state?.toUpperCase()}</p>
      </div>
      <div className="min-w-0 flex flex-row justify-between items-end">
        <div className="flex flex-col">
          <p className="truncate text-sm font-semibold text-gray-900">
            {t("MemberIdMenuLabel")}
          </p>
          <p className="truncate text-xs text-gray-500">{memberId}</p>
        </div>

        <button
          type="button"
          onClick={onDisconnect}
          className="font-mono text-gray-500 flex items-center gap-2"
          aria-label={t("DisconnectInsuranceAriaLabel", {
            label: label.toLowerCase(),
          })}
        >
          {t("DisconnectMenuLabel")}
          <ArrowRightIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

const PEEK_OFFSET = 64;

function InsuranceCardStack({
  primary,
  secondary,
  send,
  verificationResults,
}: {
  primary: NonNullable<InsuranceConnectorContext["primaryInsurance"]>;
  secondary: NonNullable<InsuranceConnectorContext["secondaryInsurance"]>;
  send: (event: InsuranceConnectorEvent) => void;
  verificationResults?: InsuranceStatus[];
}) {
  const t = useTranslations("InsuranceConnector");
  const [frontCard, setFrontCard] = useState<"primary" | "secondary">(
    "primary",
  );

  const cards = [
    {
      key: "primary" as const,
      label: t("Primary"),
      data: primary,
      disconnectEvent: "DISCONNECT_PRIMARY" as const,
    },
    {
      key: "secondary" as const,
      label: t("Secondary"),
      data: secondary,
      disconnectEvent: "DISCONNECT_SECONDARY" as const,
    },
  ];

  return (
    <div
      className="grid"
      style={{ gridTemplate: "'stack' / 1fr", paddingBottom: PEEK_OFFSET }}
    >
      {cards.map((card) => {
        const isFront = card.key === frontCard;
        return (
          <motion.div
            key={card.key}
            className={`self-start rounded-2xl ${!isFront ? "cursor-pointer" : ""}`}
            style={{ gridArea: "stack", zIndex: isFront ? 2 : 1 }}
            animate={{
              y: isFront ? PEEK_OFFSET : 0,
              scale: isFront ? 1 : 0.97,
              filter: isFront ? "brightness(1)" : "brightness(0.98)",
              boxShadow: isFront
                ? "0px 4px 24px rgba(0, 0, 0, 0.15)"
                : "0px 0px 0px rgba(0, 0, 0, 0)",
              opacity: isFront ? 1 : 0.7,
            }}
            transition={{ type: "spring", stiffness: 400, damping: 30 }}
            {...(!isFront
              ? {
                  onClick: () => setFrontCard(card.key),
                  role: "button",
                  tabIndex: 0,
                  onKeyDown: (e: React.KeyboardEvent) => {
                    if (e.key === "Enter" || e.key === " ")
                      setFrontCard(card.key);
                  },
                  "aria-label": t("BringToFrontAriaLabel", {
                    label: card.label.toLowerCase(),
                  }),
                }
              : {})}
          >
            <InsuranceCard
              label={card.label}
              payer={card.data.payer}
              planName={card.data.planName || ""}
              memberId={card.data.memberId}
              state={card.data.insuredState}
              onDisconnect={() => send({ type: card.disconnectEvent })}
              verificationResult={verificationResults?.find(
                (r) => r.memberId === card.data.memberId,
              )}
            />
          </motion.div>
        );
      })}
    </div>
  );
}

export function InsuranceMenu({ isMobile = false }: InsuranceMenuProps) {
  const context = useInsuranceConnectorContext();
  const send = useInsuranceConnectorSend();
  const consumer = useBoundStore.use.consumer();
  const t = useTranslations("InsuranceConnector");
  const menuRef = useRef<HTMLDivElement>(null);
  const navButtonRef = useNavButtonRef();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (navButtonRef?.current?.contains(target)) return;
      if (menuRef.current && !menuRef.current.contains(target)) {
        send({ type: "CLOSE" });
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [send, navButtonRef]);

  const primary = context.primaryInsurance;
  const secondary = context.secondaryInsurance;
  const verificationResults = context.lastVerificationResults;

  const primaryResult = verificationResults?.find(
    (r) => r.memberId === primary?.memberId,
  );

  return (
    <motion.div
      ref={menuRef}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className={`rounded-3xl ${isMobile ? "w-full p-4" : "w-96 p-5"}`}
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-row justify-between items-center">
          <p className="text-base font-semibold text-gray-900">
            {t("HiName", { name: consumer.fname })}
          </p>
          {!secondary && (
            <button
              type="button"
              onClick={() => send({ type: "ADD_SECONDARY" })}
              className="text-md transition-all duration-300 text-black hover:text-white hover:bg-black border border-black rounded-full px-4 py-2"
            >
              {t("AddSecondary")}
            </button>
          )}
        </div>

        {primary && secondary ? (
          <InsuranceCardStack
            primary={primary}
            secondary={secondary}
            send={send}
            verificationResults={verificationResults}
          />
        ) : primary ? (
          <div
            className="rounded-2xl"
            style={{ boxShadow: "0px 4px 24px rgba(0, 0, 0, 0.15)" }}
          >
            <InsuranceCard
              label={t("Primary")}
              payer={primary.payer}
              planName={primary.planName || ""}
              memberId={primary.memberId}
              state={primary.insuredState}
              onDisconnect={() => send({ type: "DISCONNECT_PRIMARY" })}
              verificationResult={primaryResult}
            />
          </div>
        ) : null}
      </div>
    </motion.div>
  );
}
